/**
 * The single rule for where participants may be redirected; used by the settings form,
 * the participant page and the unpublished-event path.
 * - Only absolute http(s) URLs, mirroring the backend rule, so a `javascript:` URL is
 *   never saved or followed.
 * - Never the page currently open: redirecting there would reload the page and redirect
 *   again forever (e.g. a channel set to redirect to its own share link).
 * @param value - Raw URL input or stored setting
 * @returns The trimmed URL, or null when participants must not be sent there
 */
export const resolveRedirectTarget = (value?: string | null): string | null => {
    const trimmed = value?.trim();
    if (!trimmed) {
        return null;
    }

    try {
        const target = new URL(trimmed);
        if (target.protocol !== "http:" && target.protocol !== "https:") {
            return null;
        }

        const current = new URL(window.location.href);
        const isCurrentPage =
            target.origin === current.origin &&
            target.pathname === current.pathname &&
            target.search === current.search;

        return isCurrentPage ? null : trimmed;
    } catch {
        return null;
    }
};

/**
 * Hostname shown to participants before they are redirected (e.g. "ngo.org").
 * @param value - A URL already accepted by resolveRedirectTarget
 */
export const getRedirectDomain = (value: string): string => {
    try {
        return new URL(value.trim()).hostname.replace(/^www\./, "");
    } catch {
        return value;
    }
};
